import { track, init, setUserId } from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/nextjs';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import Hotjar from '@hotjar/browser';
import { ReadonlyURLSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

const isLocal = () => {
  return location.hostname === 'localhost';
};

export const initialize = () => {
  if (isLocal()) {
    return;
  }

  init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '');

  if (
    process.env.NEXT_PUBLIC_HOTJAR_SITE_ID &&
    process.env.NEXT_PUBLIC_HOTJAR_VERSION
  ) {
    Hotjar.init(
      Number(process.env.NEXT_PUBLIC_HOTJAR_SITE_ID),
      Number(process.env.NEXT_PUBLIC_HOTJAR_VERSION),
    );
  }
};

const gtmTrack = (message: Record<string, any>) => {
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push(message);
  }
};

export const logPageView = ({
  pathname,
  searchParams,
}: {
  pathname: string;
  searchParams: ReadonlyURLSearchParams | NextParsedUrlQuery | null;
}) => {
  const amplitudeParams = {
    pathname,
    searchParams,
  };
  const gtmParams = {
    event: 'pageview',
    hostname: location.hostname,
    url: location.href.replace(location.search, ''),
    path: location.pathname,
    path_query: location.pathname,
    page_title: document.title,
  };

  if (isLocal()) {
    console.log(`[analytics] page view: ${pathname}`, {
      amplitudeParams,
      gtmParams,
    });

    return;
  }

  track('Navigation: Page view', amplitudeParams);

  gtmTrack(gtmParams);
};

export interface LogEventArgs {
  category:
    | 'Navigation'
    | 'Home'
    | 'Medicare'
    | 'Quote'
    | 'Agent Page'
    | 'Agent Directory'
    | 'Calendly Modal'
    | 'Hero'
    | 'About Video'
    | 'Careers'
    | 'Error Step'
    | 'tawk.to';
  name: string;
  [key: string]: any;
}

const dashUnderscoreRegex = /[\s-]/g;

export const logEvent = ({ category, name, ...data }: LogEventArgs) => {
  try {
    if (isLocal()) {
      console.log(`[analytics] event "${category}: ${name}"`, data);

      return;
    }

    track(`${category}: ${name}`, data);

    gtmTrack({
      event: `${category
        .toLowerCase()
        .replaceAll(dashUnderscoreRegex, '_')}_${name
        .toLowerCase()
        .replaceAll(dashUnderscoreRegex, '_')}`,
      ...data,
    });

    Hotjar.event(`${category}: ${name}`);
  } catch (error) {
    Sentry.captureException(new Error('Error processing analytics event'), {
      extra: { category, name, data, error },
    });
  }
};

export const identify = (email?: string) => {
  if (!email) {
    return;
  }

  if (isLocal()) {
    console.log(`[analytics] identify: ${email}`);

    return;
  }

  setUserId(email);

  gtmTrack({
    user_id: 'USER_ID',
  });

  Hotjar.identify(email, {});
};
